import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useLayoutStore } from '@/stores/layoutStore';
import { useNavigation } from '@/composables/useNavigation';
import type { MenuItem } from 'primevue/menuitem';
import router from '@/router';
import { isReportsRoute, REPORT_ROUTES } from '@/composables/useReportRoutes';

/** Every page under Tenants. The routes are not nested, so they are listed by name. */
const TENANT_ROUTES = new Set([
  'tenants',
  'tenantNew',
  'tenant',
  'tenantLocationNew',
  'tenantLocationDetail',
]);

/** Every page under Templates. */
const TEMPLATE_ROUTES = new Set(['tenantTemplates', 'tenantTemplateNew', 'tenantTemplate']);

export function useDrawerMenu() {
  const layoutStore = useLayoutStore();
  const { generateMenuItems } = useNavigation();
  const route = useRoute();

  const expandedKeys = ref<Record<string, boolean>>({});
  const activePopoverItems = ref<MenuItem[]>([]);

  const drawerVisible = computed(() => layoutStore.sidebarOpen);
  const menuItems = computed(() => generateMenuItems());

  // Is this menu key the page we are on? Some keys cover many pages.
  function isRouteKey(key: string | undefined): boolean {
    // Reports has many pages under it, each with its own route name.
    if (key === REPORT_ROUTES.home) return isReportsRoute(route.name);
    // So is Tenants: a client, its locations and its case tags.
    if (key === 'tenants') return TENANT_ROUTES.has(route.name as string);
    if (key === 'tenantTemplates') return TEMPLATE_ROUTES.has(route.name as string);
    return route.name === key;
  }

  // Auto-expand parent menus that contain the active route
  watchEffect(() => {
    for (const item of menuItems.value) {
      if (item.items && item.key) {
        const hasActiveChild = item.items.some((sub: MenuItem) => isRouteKey(sub.key));
        if (hasActiveChild) {
          expandedKeys.value[item.key] = true;
        }
      }
    }
  });

  function isCurrentRoute(item: MenuItem): boolean {
    if (item.command) {
      return isRouteKey(item.key);
    }
    if (item.items) {
      return item.items.some((subItem: MenuItem) => isRouteKey(subItem.key));
    }
    return false;
  }

  function isExpanded(item: MenuItem): boolean {
    return expandedKeys.value[item.key as string] || false;
  }

  function handleSignOut() {
    router.push({ name: 'logout' });
  }

  function handleCollapsedClick(
    event: Event,
    item: MenuItem,
    popover: { toggle: (e: Event) => void },
  ) {
    if (item.items) {
      activePopoverItems.value = item.items;
      popover.toggle(event);
    } else if (item.command) {
      item.command({ originalEvent: event as PointerEvent, item });
    }
  }

  function handleSubItemClick(subItem: MenuItem, popover: { hide: () => void }) {
    if (subItem.command) {
      subItem.command({ originalEvent: new PointerEvent('click'), item: subItem });
    }
    popover.hide();
  }

  function isActiveSubItem(subItem: MenuItem): boolean {
    return isRouteKey(subItem.key);
  }

  return {
    expandedKeys,
    activePopoverItems,
    drawerVisible,
    menuItems,
    isCurrentRoute,
    isExpanded,
    isActiveSubItem,
    handleSignOut,
    handleCollapsedClick,
    handleSubItemClick,
  };
}
